import type {
  SubTenant,
  SubTenantContractConnection,
  SubTenantTenantConnection,
} from '@pigello/pigello-matrix';
import type { TCustomList } from './types';
import { chunkIds } from '../utils';
import { fetchAllInstances } from '@/requests/api/fetch-all-instances';

export async function handleCustomSubTenantNestedCase(
  listResponse: TCustomList<SubTenant>
): Promise<
  TCustomList<
    SubTenant & {
      subTenantTenantConnections: SubTenantTenantConnection[];
      subTenantContractConnections: SubTenantContractConnection[];
    }
  >
> {
  const subTenantIds = new Set<string>();
  for (const subTenant of listResponse.data) {
    subTenantIds.add(subTenant.id);
  }

  const chunkedSubTenantIds = chunkIds(subTenantIds);

  const tenantConnectionPromises = Promise.all(
    chunkedSubTenantIds.map((chunk) =>
      fetchAllInstances<SubTenantTenantConnection>({
        modelName: 'subtenanttenantconnection',
        filters: {
          sub_tenant: { __in: chunk.join(',') },
        },
        order: ['-createdAt'],
      })
    )
  );

  const contractConnectionPromises = Promise.all(
    chunkedSubTenantIds.map((chunk) =>
      fetchAllInstances<SubTenantContractConnection>({
        modelName: 'subtenantcontractconnection',
        filters: {
          sub_tenant: { __in: chunk.join(',') },
        },
        order: ['-createdAt'],
      })
    )
  );

  const [resolvedTenantConnectionPromises, resolvedContractConnectionPromises] =
    await Promise.all([tenantConnectionPromises, contractConnectionPromises]);

  const allTenantConnections = resolvedTenantConnectionPromises.flatMap(
    (res) => res.list
  );

  const allContractConnections = resolvedContractConnectionPromises.flatMap(
    (res) => res.list
  );

  const mapped: Array<
    SubTenant & {
      subTenantTenantConnections: SubTenantTenantConnection[];
      subTenantContractConnections: SubTenantContractConnection[];
    }
  > = [];

  for (const subTenant of listResponse.data) {
    const tenantConnections: SubTenantTenantConnection[] = [];
    const contractConnections: SubTenantContractConnection[] = [];

    for (const tenantConnection of allTenantConnections) {
      if (tenantConnection.subTenant?.id === subTenant.id)
        tenantConnections.push(tenantConnection);
    }

    for (const contractConnection of allContractConnections) {
      if (contractConnection.subTenant?.id === subTenant.id)
        contractConnections.push(contractConnection);
    }

    mapped.push({
      ...subTenant,
      subTenantTenantConnections: tenantConnections,
      subTenantContractConnections: contractConnections,
    });
  }

  return {
    data: mapped,
  };
}
